import cn from 'classnames';
import React, { useCallback } from 'react';
import { DraggableInjectedProps } from 'components/Draggable/types';
import { cloneOnlyChild } from 'utils/react';
import { block } from '../../utils';
import { useTrackAssetContext } from './TrackAssetContext';
import TrackAssetDragHandles, {
  TrackAssetDragHandlesProps,
} from './TrackAssetDragHandles';

export type TrackAssetItemProps = Partial<DraggableInjectedProps> &
  TrackAssetDragHandlesProps & {
    children?: React.ReactNode;
    width?: React.CSSProperties['width'];
  };

const TrackAssetItem: React.FC<TrackAssetItemProps> = ({
  children,
  className,
  onMouseDown,
  onMouseUp,
  onTouchEnd,
  onTouchStart,
  handleClassName,
  resizable = true,
  style,
}) => {
  const { dragging, resizing, width } = useTrackAssetContext();

  /**
   * stop propagation on body click so that it doesn't pass through to
   * underlying timeline.
   *
   * might be better for Timeline to make this decision, but it would take some
   * work to wire up a body click from an asset all the way up to the
   * Timeline component, so putting it here for now.
   *
   * NB: onClick is called in handleDragStop.  Draggable fires onStart on
   * mouse down and onStop on mouse up, so it's the same as a click if the
   * asset hasn't moved.
   */
  const handleClick = useCallback((e: React.MouseEvent<any>) => {
    e.stopPropagation();
  }, []);

  return (
    <div
      className={cn(
        block('track-asset-item', {
          dragging,
          active: dragging || resizing,
        }),
        className,
      )}
      onClick={handleClick}
      {...{ onMouseDown, onMouseUp, onTouchEnd, onTouchStart }}
      style={{ ...style, width }}
    >
      {cloneOnlyChild(children, child => ({
        className: cn(block('track-asset-contents'), child.props.className),
      }))}
      <TrackAssetDragHandles {...{ handleClassName, resizable }} />
    </div>
  );
};

export default TrackAssetItem;
